import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, CheckCircle2, Clock, Video, LogIn, LogOut, AlertTriangle } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import { t, formatTime, formatDate } from '../i18n';
import { Button, Alert, Spinner, Card, Badge } from '../components/ui';

/**
 * Where a scanned QR lands.
 *
 * Patients mark themselves as arrived, which starts the waiting-time clock.
 * Professionals use the same code to register entry and exit.
 *
 * Online appointments never appear as checkable: there is no door to arrive at,
 * so they go straight to in_session when the professional starts.
 *
 * Every rule (window, location match, online) is enforced server-side; this
 * screen only explains the outcome.
 */

const CheckInView = () => {
  const { token } = useParams();
  const { profile, isPatient } = useSession();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: resolveError } = await supabase.rpc('resolve_checkin', { p_token: token });
    if (resolveError) setError(resolveError.message);
    else setState(data);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const checkIn = async (appointmentId) => {
    setBusyId(appointmentId);
    setError(null);
    const { data, error: checkInError } = await supabase.rpc('check_in_appointment', {
      p_appointment_id: appointmentId,
      p_token: token,
    });
    setBusyId(null);
    if (checkInError) { setError(checkInError.message); return; }
    setDone(data.already ? 'already' : 'ok');
    load();
  };

  const toggleAttendance = async () => {
    setBusyId('attendance');
    setError(null);
    const { data, error: attendanceError } = await supabase.rpc('toggle_professional_attendance', {
      p_token: token,
    });
    setBusyId(null);
    if (attendanceError) { setError(attendanceError.message); return; }
    setDone(data.action);
    load();
  };

  if (loading) return <Spinner />;

  if (!state?.found) {
    return (
      <Card>
        <Alert tone="danger" title={t('checkIn.unknownCode')}>{t('checkIn.unknownCodeBody')}</Alert>
      </Card>
    );
  }

  const { location, appointments = [] } = state;

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-900">{t('checkIn.title')}</h1>
        <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-slate-500">
          <MapPin size={14} />{location.name}
          {location.address && ` · ${location.address}`}
        </p>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {done === 'ok' && (
        <Alert tone="success" title={t('checkIn.checkedIn')}>{t('checkIn.checkedInBody')}</Alert>
      )}
      {done === 'already' && <Alert tone="info">{t('checkIn.alreadyCheckedIn')}</Alert>}
      {done === 'checked_in' && (
        <Alert tone="success">{t('checkIn.attendanceStarted', { time: formatTime(new Date()) })}</Alert>
      )}
      {done === 'checked_out' && (
        <Alert tone="info">{t('checkIn.attendanceEnded', { time: formatTime(new Date()) })}</Alert>
      )}

      {/* Professionals: the same code registers their own time in the room. */}
      {state.role === 'therapist' && (
        <Card title={t('checkIn.professionalSection')} subtitle={`${profile?.first_name} ${profile?.last_name}`}>
          {state.open_attendance ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600">
                {t('agenda.inOfficeSince', { time: formatTime(state.open_attendance.checked_in_at) })}
              </p>
              <Button variant="secondary" loading={busyId === 'attendance'} onClick={toggleAttendance}>
                <LogOut size={16} />{t('checkIn.professionalCheckOut')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600">{t('agenda.notCheckedIn')}</p>
              <Button loading={busyId === 'attendance'} onClick={toggleAttendance}>
                <LogIn size={16} />{t('checkIn.professionalCheckIn')}
              </Button>
            </div>
          )}
        </Card>
      )}

      {isPatient && (
        <Card title={t('checkIn.yourAppointmentsToday')} subtitle={formatDate(new Date())}>
          {appointments.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">{t('checkIn.noAppointmentToday')}</p>
          ) : (
            <ul className="space-y-3">
              {appointments.map((appointment) => (
                <li key={appointment.id} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {formatTime(appointment.starts_at)} · {appointment.patient}
                      </p>
                      <p className="text-xs text-slate-500">{appointment.professional}</p>
                      {appointment.status === 'waiting' && (
                        <Badge tone="warning" className="mt-1">
                          <CheckCircle2 size={12} />{t('checkIn.alreadyHere')}
                        </Badge>
                      )}
                    </div>

                    <div className="text-right">
                      {appointment.is_online ? (
                        // No arrival step exists for an online session.
                        <p className="flex items-center gap-1.5 text-sm text-slate-500">
                          <Video size={14} />{t('checkIn.onlineNoCheckIn')}
                        </p>
                      ) : !appointment.location_matches ? (
                        <p className="flex items-center gap-1.5 text-sm text-amber-700">
                          <AlertTriangle size={14} />{t('checkIn.otherLocation')}
                        </p>
                      ) : appointment.status === 'waiting' ? (
                        <p className="text-sm text-emerald-700">{t('checkIn.checkedIn')}</p>
                      ) : appointment.within_window ? (
                        <Button
                          loading={busyId === appointment.id}
                          onClick={() => checkIn(appointment.id)}
                        >
                          <CheckCircle2 size={16} />{t('checkIn.arrived')}
                        </Button>
                      ) : (
                        <p className="flex items-center gap-1.5 text-sm text-slate-500">
                          <Clock size={14} />
                          {t('checkIn.opensAt', { time: formatTime(appointment.opens_at) })}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
};

export default CheckInView;
